import { intlLocale, t } from '../i18n/index.js'

const TZ = 'Europe/Berlin'

export function formatDate(date: string): string {
  const [y, m, d] = date.split('-')
  return `${d}.${m}.${y}`
}

export function formatDateLong(date: string): string {
  return new Intl.DateTimeFormat(intlLocale(), {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    timeZone: TZ,
  }).format(new Date(`${date}T12:00:00Z`))
}

export function formatWeekday(date: string, style: 'short' | 'long' = 'short'): string {
  return new Intl.DateTimeFormat(intlLocale(), { weekday: style, timeZone: TZ }).format(new Date(`${date}T12:00:00Z`))
}

export function formatTime(iso: string | null): string {
  if (!iso) return '–'
  return new Intl.DateTimeFormat(intlLocale(), { hour: '2-digit', minute: '2-digit', timeZone: TZ }).format(new Date(iso))
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '–'
  return new Intl.DateTimeFormat(intlLocale(), { dateStyle: 'short', timeStyle: 'short', timeZone: TZ }).format(
    new Date(iso),
  )
}

export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date())
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function startOfWeek(date: string): string {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay()
  return addDays(date, weekday === 0 ? -6 : 1 - weekday)
}

export function relativeDayLabel(date: string, today = todayIso()): string {
  if (date === today) return t('day.today')
  if (date === addDays(today, 1)) return t('day.tomorrow')
  if (date === addDays(today, -1)) return t('day.yesterday')
  return formatDate(date)
}

export const STATUS_LABEL: Record<string, string> = {
  open: 'offen',
  done: 'erledigt',
  missed: 'nicht erledigt',
  final_missed: 'endgültig nicht erledigt',
  skipped: 'entfallen',
}

export const ISSUE_STATUS_LABEL: Record<string, string> = {
  open: 'offen',
  in_progress: 'in Arbeit',
  done: 'erledigt',
  rejected: 'abgelehnt',
}

export const PRIORITY_LABEL: Record<string, string> = { low: 'niedrig', normal: 'normal', high: 'hoch' }

export const PHOTO_REQUIREMENT_LABEL: Record<string, string> = {
  none: 'kein Foto',
  after: 'Foto nachher',
  before_after: 'Foto vorher + nachher',
}

// ---------------------------------------------------------------- Monatsraster

export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`
}

export function endOfMonth(date: string): string {
  const [year, month] = date.split('-').map(Number) as [number, number]
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return `${date.slice(0, 7)}-${String(last).padStart(2, '0')}`
}

export function addMonths(date: string, delta: number): string {
  const [year, month] = date.split('-').map(Number) as [number, number]
  const d = new Date(Date.UTC(year, month - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`
}
